import {
  Flex,
  Box,
  HTMLChakraProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react';
import { useResourceRelationship, useResources } from 'datx-jsonapi-react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, Fragment, useState, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { useTranslation } from 'react-i18next';
import { RemoveScroll } from 'react-remove-scroll';
import { useKey } from 'react-use';

import MenuIcon from '@/assets/icons/ic-menu.svg';
import ExpoIcon from '@/assets/icons/ic-nav-expo.svg';
import HelpDeskIcon from '@/assets/icons/ic-nav-help-desk.svg';
import LoungeIcon from '@/assets/icons/ic-nav-lounge.svg';
import MainStageIcon from '@/assets/icons/ic-nav-main-stage.svg';
import PostersIcon from '@/assets/icons/ic-nav-posters.svg';
import ReceptionIcon from '@/assets/icons/ic-nav-reception.svg';
import ScheduleIcon from '@/assets/icons/ic-nav-schedule.svg';
import SessionsIcon from '@/assets/icons/ic-nav-sessions.svg';
import UnderlineIcon from '@/assets/icons/ic-nav-underline.svg';
import UserIcon from '@/assets/icons/ic-user.svg';

import { Overlay } from '@/components/ui/core/Overlay/Overlay';
import { SecondaryMenuDesktop } from '@/components/ui/shared/headers/EventHeader/layouts/SecondaryMenu.desktop';
import { SecondaryMenuMobile } from '@/components/ui/shared/headers/EventHeader/layouts/SecondaryMenu.mobile';
import { NAVIGATION_ITEMS } from '@/constants/event-app-navigation-items';
import { selfServiceEventsURL } from '@/constants/self-service-dashboard-routes';
import useSession from '@/hooks/useSession';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EventConfiguration } from '@/resources/EventConfiguration';
import { EventTicket } from '@/resources/EventTicket';
import { ThinEvent } from '@/resources/ThinEvent';
import { ThinProfile } from '@/resources/ThinProfile';
import { User } from '@/resources/User';

import { EventNavigationItem } from './components/EventNavigationItem/EventNavigationItem';
import {
  StyledHeader,
  StyledHeading,
  StyledNavLink,
  StyledNavigation,
  StyledNavigationItemLabel,
  StyledLogoContainer,
  StyledMenuButton,
} from './EventHeader.elements';

const iconLookupTable = new Map([
  ['reception', ReceptionIcon],
  ['schedule', ScheduleIcon],
  ['mainStage', MainStageIcon],
  ['sessions', SessionsIcon],
  ['posters', PostersIcon],
  ['expo', ExpoIcon],
  ['lounge', LoungeIcon],
  ['helpdesk', HelpDeskIcon],
]);

export interface IEventHeaderProps extends HTMLChakraProps<'div'> {
  event: ThinEvent;
  currentUserProfile: ThinProfile;
  onLogout: () => void;
}

export const EventHeader: FC<IEventHeaderProps> = ({ event, currentUserProfile, onLogout, ...rest }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const headerRef = useRef();
  const { user } = useSession();

  const [shouldPrimaryNavDrawerShow] = useMediaQuery(`(max-width: ${breakpoints.lg})`);
  const [isPrimaryNavOpen, setIsPrimaryNavOpen] = useState(false);

  useKey('Escape', () => setIsPrimaryNavOpen(false));

  const { data: eventConfiguration, error: configError } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(event?.id)
  );

  const { data: tickets, error: ticketsError } = useResources(() =>
    user?.id && event?.id
      ? [
          EventTicket,
          {
            filter: {
              event_id: event.id,
              user_id: user.id,
            },
          },
        ]
      : null
  );

  const isTicketsLoading = !tickets && !ticketsError;
  const isUserWithTicket = !isTicketsLoading && user && tickets?.length > 0;

  // @ts-ignore: Object is possibly 'null'.
  const headerBounding = headerRef?.current?.getBoundingClientRect();

  // TODO: should close sidenav on route switch
  // not important atm since all the pages render their own layout

  return (
    <StyledHeader ref={headerRef} {...rest}>
      <Overlay
        zIndex={1000}
        hidden={!shouldPrimaryNavDrawerShow || !isPrimaryNavOpen}
        onClick={() => setIsPrimaryNavOpen(false)}
      />

      <StyledMenuButton onClick={() => setIsPrimaryNavOpen(true)}>
        <MenuIcon height="1.5rem" width="1.5rem" />
      </StyledMenuButton>

      <RemoveScroll enabled={shouldPrimaryNavDrawerShow && isPrimaryNavOpen} forwardProps>
        <Box className="scroll" zIndex={1000}>
          <FocusLock
            as={StyledNavigation}
            disabled={!shouldPrimaryNavDrawerShow || !isPrimaryNavOpen}
            lockProps={{ 'aria-expanded': isPrimaryNavOpen }}
          >
            <Fragment>
              {!eventConfiguration
                ? NAVIGATION_ITEMS.map(({ id }) => (
                    <Skeleton key={id} h="14" w="14" flexShrink={0} _notLast={{ mb: 2 }} />
                  ))
                : NAVIGATION_ITEMS.filter(({ id }) => eventConfiguration[`${id}Page`]).map(({ id, route }) => {
                    const Icon = iconLookupTable.get(id);

                    return (
                      <NextLink key={id} href={`/events/${router.query.eventId}${route}`} passHref legacyBehavior>
                        <EventNavigationItem
                          isActive={router.asPath.includes(route)}
                          Icon={<Icon height="1.5rem" width="1.5rem" />}
                          enableTooltip={!shouldPrimaryNavDrawerShow}
                          aria-label={`Go to${eventConfiguration[`${id}PageLabel`]}` || id}
                          mb="2"
                        >
                          <StyledNavigationItemLabel>
                            {eventConfiguration[`${id}PageLabel`] || id}
                          </StyledNavigationItemLabel>
                        </EventNavigationItem>
                      </NextLink>
                    );
                  })}

              {user ? (
                <Fragment>
                  <Box mt="auto" />
                  {currentUserProfile && (
                    <NextLink href="/my-account/profile" passHref legacyBehavior>
                      <EventNavigationItem
                        enableTooltip={!shouldPrimaryNavDrawerShow}
                        Icon={
                          currentUserProfile?.mediumSquareAvatarUrl ? (
                            <NextImage
                              width={24}
                              height={24}
                              src={currentUserProfile.mediumSquareAvatarUrl}
                              alt={currentUserProfile.fullName}
                              style={{ borderRadius: '50%' }}
                            />
                          ) : (
                            <UserIcon height="1.5rem" />
                          )
                        }
                        display={{ base: 'inherit', lg: 'none' }}
                      >
                        <StyledNavigationItemLabel>{t('action.myProfile')}</StyledNavigationItemLabel>
                      </EventNavigationItem>
                    </NextLink>
                  )}
                  {user.isEventManager ? (
                    <StyledNavLink href={selfServiceEventsURL}>{t('action.myOrganizedEvents')}</StyledNavLink>
                  ) : null}
                  <StyledNavLink as="button" onClick={onLogout}>
                    {t('action.logOut')}
                  </StyledNavLink>
                </Fragment>
              ) : (
                <NextLink href={`/log-in?redirectUrl=${router.asPath}`} passHref legacyBehavior>
                  <StyledNavLink mt="auto">{t('action.logIn')}</StyledNavLink>
                </NextLink>
              )}

              <NextLink href="/" passHref legacyBehavior>
                <EventNavigationItem
                  enableTooltip={!shouldPrimaryNavDrawerShow}
                  Icon={<UnderlineIcon height="1.5rem" width="1.5rem" />}
                  mt={{ base: 2, lg: 'auto' }}
                  aria-label="Go to homepage"
                  minH="3.5rem"
                >
                  <StyledNavigationItemLabel>{t('underline')}</StyledNavigationItemLabel>
                </EventNavigationItem>
              </NextLink>
            </Fragment>
          </FocusLock>
        </Box>
      </RemoveScroll>

      <StyledLogoContainer>
        {event?.smallLogoUrl && <NextImage width={48} height={48} src={event.smallLogoUrl} alt={event.name} />}
        {!event?.smallLogoUrl && <Skeleton w={12} h={12} />}
      </StyledLogoContainer>

      <StyledHeading as="h1" noOfLines={1} minW="0">
        {event?.name || <Skeleton h={['1.5rem', null, '1.75rem', '1.5rem']} w="10.5rem" />}
      </StyledHeading>

      {isUserWithTicket && (
        <SecondaryMenuMobile ml="auto" display={{ base: 'block', lg: 'none' }} key={router.asPath} />
      )}

      <Flex ml="auto" mr="5" display={{ base: 'none', lg: 'inherit' }} alignItems="center">
        {isUserWithTicket && (
          <SecondaryMenuDesktop
            display={{ base: 'none', lg: 'flex' }}
            key={router.asPath}
            headerHeight={headerBounding?.height}
          />
        )}
        <Menu variant="navigation" placement="bottom-end">
          <MenuButton
            ml="5"
            h={8}
            w={8}
            display="flex"
            justifyContent="center"
            alignItems="center"
            aria-label="Profile button"
            p={1}
            _hover={{
              bg: 'gray.200',
              boxShadow: '0 0 0 0.625rem #CACCCC',
            }}
            _focus={{
              bg: 'gray.200',
              boxShadow: '0 0 0 0.625rem #CACCCC !important',
            }}
          >
            {currentUserProfile?.mediumSquareAvatarUrl ? (
              <NextImage
                width={24}
                height={24}
                src={currentUserProfile.mediumSquareAvatarUrl}
                alt={currentUserProfile.fullName}
                style={{ borderRadius: '50%' }}
              />
            ) : (
              <UserIcon />
            )}
          </MenuButton>
          <MenuList>
            {user ? (
              <Fragment>
                {currentUserProfile && (
                  <NextLink href="/my-account/profile" passHref legacyBehavior>
                    <MenuItem as="a">{t('action.myProfile')}</MenuItem>
                  </NextLink>
                )}
                {user.isEventManager ? (
                  <MenuItem as="a" href={selfServiceEventsURL}>
                    {t('action.myOrganizedEvents')}
                  </MenuItem>
                ) : null}
                <MenuItem onClick={onLogout}>{t('action.logOut')}</MenuItem>
              </Fragment>
            ) : (
              <NextLink href={`/log-in?redirectUrl=${router.asPath}`} passHref legacyBehavior>
                <MenuItem as="a"> {t('action.logIn')}</MenuItem>
              </NextLink>
            )}
          </MenuList>
        </Menu>
      </Flex>
    </StyledHeader>
  );
};
